// OAuth 2.0 authorization-code + PKCE against the Genesys Auth0 client.
// Two flows, one module:
//
//   1. `requestGenesysAuthCode` — silent iframe with `prompt=none`. This
//      is what Genesys' `AuthProvider.getAuthCode` command resolves
//      against. Reuses the app's existing SSO cookie, no visible chrome,
//      no state loss. Standard OIDC pattern (`@auth0/auth0-spa-js`'s
//      `checkSession` uses the same protocol under the hood). If the
//      callback carries `login_required` / `interaction_required` /
//      `consent_required` / `account_selection_required`, we throw a
//      `SilentInteractionRequiredError` so the caller can escalate to
//      the full-page fallback.
//
//   2. `redirectToGenesysAuth0` — full-page redirect. Escape hatch used
//      by the bridge's `onSessionFailure` when the WebSocket surfaces a
//      403 that silent-iframe can't repair (Genesys backend rejected a
//      seemingly-valid token, or the SSO cookie itself needs a fresh
//      user sign-in). On return, the callback stashes the code in
//      sessionStorage; the next `requestGenesysAuthCode` invocation
//      consumes it before falling through to silent iframe.
//
// This module DOES NOT exchange the code for a token — the Genesys
// widget does that itself against the deployment's registered IdP once
// we return `{ authCode, redirectUri, codeVerifier }` from the
// `AuthProvider.getAuthCode` command.

import { AUTH0_CONNECTION } from "common/constants";
import type { GenesysAuthCode } from "./genesys-messenger";

// ---------------------------------------------------------------------
// Per-env values. `GENESYS_OAUTH_CLIENT_ID` is a distinct Auth0 client
// from `VITE_AUTH0_CLIENT_ID` (the app's login client) — Identity
// provisions a separate "Regular Web Application" whose callback list
// is allow-listed for `${origin}${REDIRECT_PATH}`, and whose grant is
// registered as the deployment's IdP in Genesys Cloud Admin.
// ---------------------------------------------------------------------
const GENESYS_OAUTH_CLIENT_ID = import.meta.env.VITE_GENESYS_OAUTH_CLIENT_ID as
  | string
  | undefined;

// Auth0 host — same tenant as the app's regular login by default, so
// the SSO cookie set by app login satisfies `prompt=none`. Override
// only if Genesys is registered against a different Auth0 tenant, in
// which case silent won't work (cross-tenant, no shared cookie).
const GENESYS_AUTH_HOST =
  (import.meta.env.VITE_GENESYS_AUTH_HOST as string | undefined) ??
  (import.meta.env.VITE_AUTH0_DOMAIN as string | undefined);

// Auth0 connection to force. Falls back to the shared `AUTH0_CONNECTION`
// constant so we don't duplicate the connection name literal across the
// codebase.
const GENESYS_AUTH_CONNECTION =
  (import.meta.env.VITE_GENESYS_AUTH_CONNECTION as string | undefined) ??
  AUTH0_CONNECTION;

// The audience the token should be issued for — matches the API
// identifier of the Genesys Auth0 API.
const GENESYS_AUDIENCE =
  (import.meta.env.VITE_GENESYS_AUDIENCE as string | undefined) ??
  "genesys-live-chat";

// Static HTML in `public/` that receives the redirect. Dual-mode: in an
// iframe it postMessages back to the parent; standalone it stashes the
// result in sessionStorage and navigates to `/home`. Whatever value we
// register with Auth0 must match this exactly.
const GENESYS_REDIRECT_PATH =
  (import.meta.env.VITE_GENESYS_REDIRECT_PATH as string | undefined) ??
  "/genesys-oauth-callback.html";
// ---------------------------------------------------------------------

const AUTHORIZE_PATH = "/authorize";
const SCOPE = "openid profile email offline_access";
const RESPONSE_TYPE = "code";
const CODE_CHALLENGE_METHOD = "S256";

// Silent-iframe flow ceiling. Auth0 hosts the entire silent negotiation
// server-side — no page renders, no user interaction — so ten seconds
// is generous headroom for the RTT + redirect chain. Beyond that, treat
// it as blocked (CSP, iframe policy, missing SSO cookie the redirect
// page couldn't reach us from) and surface a timeout error. Env
// override exists purely for tests — production keeps the default.
const SILENT_TIMEOUT_MS = Number(
  import.meta.env.VITE_GENESYS_OAUTH_SILENT_TIMEOUT_MS ?? 10_000
);

// Auth0 (and OIDC in general) surfaces "we would have needed to prompt
// the user, but you asked for silent" as one of these error codes. These
// get wrapped in `SilentInteractionRequiredError` so the caller can tell
// them apart from generic protocol/config errors — the right recovery
// is a full-page redirect (`redirectToGenesysAuth0`), not another
// silent attempt.
const SILENT_INTERACTION_ERRORS = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
  "account_selection_required",
]);

// sessionStorage keys for the redirect handoff. Callback HTML writes
// `PENDING_REDIRECT_CALLBACK_STORAGE_KEY` with the parsed URL params;
// `requestGenesysAuthCode` reads and consumes it on the next invocation
// before falling through to silent iframe. The state/verifier are
// stashed BEFORE the redirect fires so the callback can be validated
// (CSRF) when the browser returns.
const PENDING_REDIRECT_CALLBACK_STORAGE_KEY =
  "emblem:genesys:oauth:pendingRedirectCallback";
const PENDING_REDIRECT_STATE_STORAGE_KEY =
  "emblem:genesys:oauth:pendingRedirectState";
const PENDING_REDIRECT_VERIFIER_STORAGE_KEY =
  "emblem:genesys:oauth:pendingRedirectVerifier";

// Shape both the iframe postMessage payload and the sessionStorage
// stash use. Kept in sync with `public/genesys-oauth-callback.html`.
const CALLBACK_MESSAGE_TYPE = "genesys-oauth-callback";

type CallbackMessage =
  | { type: typeof CALLBACK_MESSAGE_TYPE; code: string; state: string }
  | {
      type: typeof CALLBACK_MESSAGE_TYPE;
      error: string;
      errorDescription?: string;
      state?: string;
    };

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  // Array.from + map avoids the for-of restriction; `String.fromCharCode`
  // takes one byte at a time so the join produces the same binary string.
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
    ""
  );
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** 32 random bytes, base64url-encoded — a valid PKCE verifier per RFC 7636 §4.1. */
function randomUrlSafeToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input)
  );
  return base64UrlEncode(new Uint8Array(digest));
}

function buildAuthorizeUrl({
  codeChallenge,
  state,
  redirectUri,
  silent,
}: {
  codeChallenge: string;
  state: string;
  redirectUri: string;
  silent: boolean;
}): string {
  const params: Record<string, string> = {
    client_id: GENESYS_OAUTH_CLIENT_ID!,
    response_type: RESPONSE_TYPE,
    audience: GENESYS_AUDIENCE,
    scope: SCOPE,
    code_challenge: codeChallenge,
    code_challenge_method: CODE_CHALLENGE_METHOD,
    connection: GENESYS_AUTH_CONNECTION,
    redirect_uri: redirectUri,
    state,
  };
  if (silent) {
    // `prompt=none` tells Auth0 not to render any UI: if the app's SSO
    // cookie already covers this audience, immediately redirect with
    // the code; otherwise redirect with `error=login_required`. Never
    // a login page, so it's safe to render inside a hidden iframe.
    params.prompt = "none";
  }
  return `https://${GENESYS_AUTH_HOST}${AUTHORIZE_PATH}?${new URLSearchParams(
    params
  ).toString()}`;
}

function safeSessionRead(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Wipes the three sessionStorage keys used to bridge a full-page OAuth
 * redirect back into `requestGenesysAuthCode` (`{code, state}`, expected
 * state, and PKCE verifier). Called on logout so a subsequent member on
 * the same tab can't consume a prior member's callback.
 */
export function clearRedirectHandoffStorage(): void {
  try {
    window.sessionStorage.removeItem(PENDING_REDIRECT_CALLBACK_STORAGE_KEY);
    window.sessionStorage.removeItem(PENDING_REDIRECT_STATE_STORAGE_KEY);
    window.sessionStorage.removeItem(PENDING_REDIRECT_VERIFIER_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — nothing to do.
  }
}

/**
 * Consumes a pending redirect callback from sessionStorage, if any. The
 * callback HTML writes `{code, state}` (or an error envelope) into
 * `PENDING_REDIRECT_CALLBACK_STORAGE_KEY` when it's loaded top-level
 * (i.e. after a full-page redirect); the next `requestGenesysAuthCode`
 * invocation calls this to pick the code up. Validates state against
 * the value stashed before the redirect (CSRF), clears all three keys
 * after read.
 *
 * Returns `null` when there's nothing to consume. Throws when the
 * payload carries an error or the CSRF check fails.
 */
function consumePendingRedirectCallback(
  redirectUri: string
): GenesysAuthCode | null {
  const raw = safeSessionRead(PENDING_REDIRECT_CALLBACK_STORAGE_KEY);
  if (!raw) return null;

  const expectedState = safeSessionRead(PENDING_REDIRECT_STATE_STORAGE_KEY);
  const codeVerifier = safeSessionRead(PENDING_REDIRECT_VERIFIER_STORAGE_KEY);
  clearRedirectHandoffStorage();

  let payload: CallbackMessage | undefined;
  try {
    payload = JSON.parse(raw) as CallbackMessage;
  } catch {
    throw new Error("Genesys OAuth redirect payload was malformed.");
  }
  if (!payload || payload.type !== CALLBACK_MESSAGE_TYPE) {
    throw new Error("Genesys OAuth redirect payload had an unexpected shape.");
  }
  if (!expectedState || payload.state !== expectedState) {
    throw new Error(
      "Genesys OAuth redirect state mismatch — refusing to use the returned code."
    );
  }
  if ("error" in payload) {
    throw new Error(
      `Genesys OAuth redirect failed: ${payload.error}${
        payload.errorDescription ? ` — ${payload.errorDescription}` : ""
      }`
    );
  }
  if (!codeVerifier) {
    throw new Error(
      "Genesys OAuth redirect completed but the PKCE verifier is missing — cannot exchange the code."
    );
  }
  return { authCode: payload.code, redirectUri, codeVerifier };
}

/**
 * Thrown when Auth0 says the user has to interact (SSO cookie
 * missing/expired, consent required, account selection). The right
 * recovery is a full-page redirect (`redirectToGenesysAuth0`) — a
 * silent retry from the same page can't repair the SSO cookie itself.
 */
export class SilentInteractionRequiredError extends Error {
  readonly code: string;

  constructor(code: string, description?: string) {
    super(
      `Genesys silent OAuth needs interaction: ${code}${
        description ? ` — ${description}` : ""
      }`
    );
    this.name = "SilentInteractionRequiredError";
    this.code = code;
  }
}

function awaitIframeCallback({
  iframe,
  expectedState,
  timeoutMs,
}: {
  iframe: HTMLIFrameElement;
  expectedState: string;
  timeoutMs: number;
}): Promise<{ authCode: string }> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    function cleanup() {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      window.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }

    function onMessage(event: MessageEvent) {
      // Reject cross-origin postMessages outright — the callback page
      // is served from our own origin.
      if (event.origin !== window.location.origin) return;
      const data = event.data as CallbackMessage | undefined;
      if (!data || data.type !== CALLBACK_MESSAGE_TYPE) return;
      // State check BEFORE cleanup — cleanup() destroys the iframe and
      // detaches the listener, so a mismatched-state message from a
      // sibling handshake (concurrent silent auth from another flow, or
      // a lingering redirect from a prior in-flight attempt) would
      // otherwise tear down our real flow and reject its promise. Now
      // we silently ignore anything whose state doesn't match ours —
      // if our own callback never arrives, the outer timeout is the
      // safety net. Also drops error messages that lack state entirely
      // (Auth0 omits state on some malformed-request errors) since we
      // can't attribute them to this specific handshake.
      if (!("state" in data) || data.state !== expectedState) return;
      cleanup();
      if ("error" in data) {
        if (SILENT_INTERACTION_ERRORS.has(data.error)) {
          reject(
            new SilentInteractionRequiredError(
              data.error,
              data.errorDescription
            )
          );
          return;
        }
        reject(
          new Error(
            `Genesys silent OAuth failed: ${data.error}${
              data.errorDescription ? ` — ${data.errorDescription}` : ""
            }`
          )
        );
        return;
      }
      resolve({ authCode: data.code });
    }

    window.addEventListener("message", onMessage);

    // Iframe has no cross-origin `error`/`load` guarantees for the
    // auth navigation (browsers hide those to prevent origin probing),
    // so silent failure looks like nothing happening. The timeout is
    // the only reliable way out.
    timer = setTimeout(() => {
      cleanup();
      reject(new Error("Genesys silent OAuth timed out."));
    }, timeoutMs);
  });
}

/**
 * Full-page redirect to Auth0's authorize endpoint. Stashes the PKCE
 * verifier + state in sessionStorage so the callback HTML's handoff and
 * the next `requestGenesysAuthCode` invocation can validate and reuse
 * them, then navigates the whole tab. Never resolves — the returned
 * promise sits pending until the browser tears the page down.
 */
async function redirectToAuth0(redirectUri: string): Promise<never> {
  const codeVerifier = randomUrlSafeToken();
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomUrlSafeToken();

  try {
    window.sessionStorage.setItem(PENDING_REDIRECT_STATE_STORAGE_KEY, state);
    window.sessionStorage.setItem(
      PENDING_REDIRECT_VERIFIER_STORAGE_KEY,
      codeVerifier
    );
  } catch (err) {
    throw new Error(
      `Genesys OAuth redirect could not stash CSRF/PKCE state: ${
        err instanceof Error ? err.message : String(err)
      }`
    );
  }

  const authorizeUrl = buildAuthorizeUrl({
    codeChallenge,
    state,
    redirectUri,
    silent: false,
  });
  window.location.assign(authorizeUrl);
  // `assign` triggers navigation asynchronously; return a never-
  // resolving promise so the caller's `await` doesn't fall through to
  // downstream logic before the browser tears the page down.
  return new Promise<never>(() => {});
}

// ---------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------

/**
 * Escape hatch used by the bridge's `onSessionFailure` when silent
 * iframe can't repair the auth state (WebSocket surfaced a persistent
 * 403, SSO cookie is stale). Same underlying redirect + callback flow;
 * on return, the next `requestGenesysAuthCode` invocation picks the
 * code up from sessionStorage before falling through to silent iframe.
 */
export async function redirectToGenesysAuth0(): Promise<never> {
  if (!GENESYS_OAUTH_CLIENT_ID || !GENESYS_AUTH_HOST) {
    throw new Error(
      "Genesys OAuth is not configured — set VITE_GENESYS_OAUTH_CLIENT_ID and VITE_GENESYS_AUTH_HOST (or VITE_AUTH0_DOMAIN)."
    );
  }
  const redirectUri = `${window.location.origin}${GENESYS_REDIRECT_PATH}`;
  return redirectToAuth0(redirectUri);
}

// Serialisation chain for `requestGenesysAuthCode`. Genesys' SDK can
// invoke `AuthProvider.getAuthCode` and `AuthProvider.reAuthenticate`
// close together (initial handshake racing the session-start watchdog,
// tab-wake refresh racing a queued CTA, double-tap on the CTA). Without
// serialisation each caller mounts its own iframe + generates its own
// PKCE verifier — two auth codes produced in parallel, both handed to
// Genesys, one wins the code-exchange POST and the other 403s. Chaining
// forces each invocation to wait for the previous one's PKCE round-trip
// to settle before starting its own; each caller still gets a fresh
// verifier + code, but never concurrently. Errors in a prior call don't
// poison later ones (the second `doRequest` in `.then(_, _)` swallows).
let authCodeChain: Promise<unknown> = Promise.resolve();

async function requestGenesysAuthCodeUnserialised(): Promise<GenesysAuthCode> {
  if (!GENESYS_OAUTH_CLIENT_ID || !GENESYS_AUTH_HOST) {
    throw new Error(
      "Genesys OAuth is not configured — set VITE_GENESYS_OAUTH_CLIENT_ID and VITE_GENESYS_AUTH_HOST (or VITE_AUTH0_DOMAIN)."
    );
  }

  const redirectUri = `${window.location.origin}${GENESYS_REDIRECT_PATH}`;

  const pending = consumePendingRedirectCallback(redirectUri);
  if (pending) return pending;

  const codeVerifier = randomUrlSafeToken();
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomUrlSafeToken();

  const authorizeUrl = buildAuthorizeUrl({
    codeChallenge,
    state,
    redirectUri,
    silent: true,
  });

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("title", "Genesys silent authentication");
  iframe.style.cssText =
    "position:absolute;width:0;height:0;border:0;visibility:hidden;left:-9999px;top:-9999px;";
  iframe.src = authorizeUrl;
  document.body.appendChild(iframe);

  const { authCode } = await awaitIframeCallback({
    iframe,
    expectedState: state,
    timeoutMs: SILENT_TIMEOUT_MS,
  });
  return { authCode, redirectUri, codeVerifier };
}

/**
 * Runs OAuth 2.0 authorization-code + PKCE against the Genesys Auth0
 * client. Two-stage strategy:
 *
 *   1. If a previous invocation called `redirectToGenesysAuth0` and the
 *      browser has since returned to the callback URL, consume the code
 *      the callback HTML stashed in sessionStorage and return it
 *      immediately — no network round-trip needed.
 *   2. Otherwise, run silent iframe with `prompt=none`. Reuses the
 *      app's Auth0 SSO cookie, no user interaction, no state loss.
 *
 * Concurrent invocations are serialised — see `authCodeChain` above.
 *
 * Rejects when:
 *   - Env is incomplete (client_id / auth host missing)
 *   - The pending callback carries an OAuth error
 *   - The returned `state` doesn't match (CSRF guard)
 *   - Auth0 says the user must interact — thrown as
 *     `SilentInteractionRequiredError` so the caller (`getAuthCode` →
 *     `MessagingService.error` → bridge `onSessionFailure`) can
 *     escalate to `redirectToGenesysAuth0`.
 *   - Iframe times out or the callback carries a non-interaction error.
 */
export function requestGenesysAuthCode(): Promise<GenesysAuthCode> {
  const next = authCodeChain.then(
    () => requestGenesysAuthCodeUnserialised(),
    () => requestGenesysAuthCodeUnserialised()
  );
  authCodeChain = next.catch(() => undefined);
  return next;
}
